<template>
  <div class="flex items-center space-x-1">
      <!-- Activation/Deactivation toggle -->
      <Tooltip>
        <TooltipTrigger>
          <Button
            variant="ghost"
            size="sm"
            class="text-muted-foreground hover:text-foreground"
            :disabled="isTogglingActivation"
            @click="toggleActivation"
          >
            <Pause v-if="props.mcpServer.isActive" class="h-4 w-4" />
            <Play v-else class="h-4 w-4" />
            <span class="sr-only">{{
              props.mcpServer.isActive
                ? t("settings.mcpServers.table.actions.disable")
                : t("settings.mcpServers.table.actions.enable")
            }}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            {{
              props.mcpServer.isActive
                ? t("settings.mcpServers.table.actions.disable")
                : t("settings.mcpServers.table.actions.enable")
            }}
          </p>
        </TooltipContent>
      </Tooltip>

      <!-- Test action -->
      <Popover>
        <PopoverTrigger>
          <Tooltip>
            <TooltipTrigger>
              <Button
                variant="ghost"
                size="sm"
                class="text-muted-foreground hover:text-foreground"
              >
                <Plug class="h-4 w-4" />
                <span class="sr-only">{{
                  t("settings.mcpServers.table.actions.test")
                }}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{{ t("settings.mcpServers.table.actions.test") }}</p>
            </TooltipContent>
          </Tooltip>
        </PopoverTrigger>
        <PopoverContent>
          <McpServersTest :mcp-server="props.mcpServer" />
        </PopoverContent>
      </Popover>

      <!-- Edit action -->
      <McpServersEditDialog :mcp-server="props.mcpServer">
        <Tooltip>
          <TooltipTrigger>
            <Button
              variant="ghost"
              size="sm"
              class="text-muted-foreground hover:text-foreground"
            >
              <Pencil class="h-4 w-4" />
              <span class="sr-only">{{
                t("settings.mcpServers.table.actions.edit")
              }}</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{{ t("settings.mcpServers.table.actions.edit") }}</p>
          </TooltipContent>
        </Tooltip>
      </McpServersEditDialog>

      <!-- Delete action -->
      <McpServersDeleteDialog
        :mcp-server="props.mcpServer"
        @confirmed="handleDeleteConfirm"
      >
        <Tooltip>
          <TooltipTrigger>
            <Button
              variant="ghost"
              size="sm"
              class="text-muted-foreground hover:text-foreground"
            >
              <Trash class="h-4 w-4" />
              <span class="sr-only">
                {{ t("settings.mcpServers.table.actions.delete") }}
              </span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{{ t("settings.mcpServers.table.actions.delete") }}</p>
          </TooltipContent>
        </Tooltip>
      </McpServersDeleteDialog>
    </div>
</template>

<script setup lang="ts">
import { Pause, Pencil, Play, Plug, Trash } from "lucide-vue-next";
import { refreshApiMcpServersData } from "~/composables/api";
import type { ApiMcpServer } from "~/server/api/organizations/[organizationId]/mcpServers/types";

const { t } = useI18n();

const props = defineProps<{
  mcpServer: ApiMcpServer;
}>();

const { organizationId } = useOrganizationId();

const isTogglingActivation = ref(false);

const toggleActivation = async () => {
  try {
    isTogglingActivation.value = true;
    await $fetch(
      `/api/organizations/${organizationId.value}/mcpServers/${props.mcpServer.id}`,
      {
        method: "PUT",
        body: {
          name: props.mcpServer.name,
          url: props.mcpServer.url,
          authorizationHeader: props.mcpServer.authorizationHeader,
          isActive: !props.mcpServer.isActive
        }
      }
    );

    await refreshApiMcpServersData();
  } catch (error) {
    console.error("Failed to toggle MCP server activation:", error);
  } finally {
    isTogglingActivation.value = false;
  }
};

const handleDeleteConfirm = async (mcpServer: ApiMcpServer) => {
  try {
    await $fetch(
      `/api/organizations/${organizationId.value}/mcpServers/${mcpServer.id}`,
      {
        method: "DELETE"
      }
    );

    await refreshApiMcpServersData();
  } catch (error) {
    console.error("Failed to delete MCP server:", error);
  }
};
</script>
